'use client';

import {
  amplitudeSendEvent,
  GTM_NOTSET_STRING,
  getScreenName,
  pushDataLayer,
} from '@musinsa/frontend-utils';
import { RollingBanner } from '@musinsa/mds-ui';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import { useQuery } from '@tanstack/react-query';
import { twMerge } from 'tailwind-merge';
import { curatorQueries } from '../../query-factory';
import { SNOWPLOW_SCHEME } from '../../snowplow';

export interface NoticeBannerSectionProps {
  className?: string;
}

export const NoticeBannerSection = ({ className }: NoticeBannerSectionProps) => {
  const { service } = useDeviceInfo();
  const { data: mypageData, isPending, error } = useQuery(curatorQueries.mypage.data());

  const noticeBanners = mypageData?.noticeBanners;

  // 에러 상태 처리
  if (error) {
    console.error('NoticeBanner 데이터 로딩 실패:', error);
    return null;
  }

  // 로딩 상태에서는 스켈레톤 표시
  if (isPending) {
    return (
      <section className={twMerge('w-full px-[16px]! pt-[4px]! pb-[12px]!', className)}>
        <div className="h-[40px] bg-gray-200 rounded animate-pulse" />
      </section>
    );
  }

  // 데이터가 없거나 noticeBanners가 비어있으면 null 반환
  if (!noticeBanners || !noticeBanners.length) {
    return null;
  }

  // RollingBanner를 위한 데이터 변환
  const banners = noticeBanners.map((banner, index) => ({
    key: index,
    label: banner.label,
    text: banner.title,
  }));

  const handleClickBanner = (currentIndex: number) => {
    const currentBanner = noticeBanners[currentIndex];
    if (!currentBanner) {
      return;
    }

    if (service === SERVICE['29CM']) {
      track({
        schema: SNOWPLOW_SCHEME.CLICK_CONTENT,
        data: {
          page_name: 'curator_mypage',
          content_no: currentBanner.id,
          section_name: 'Notice',
          position: 1,
          section_title: currentBanner.title,
          layout_position: currentIndex + 1,
        },
      });
    }

    if (service === SERVICE.MUSINSA) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'top_banner',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        name: currentBanner.title,
        section_name: 'mypage_top_banner',
      });
      pushDataLayer('click_content', {
        brand_id: GTM_NOTSET_STRING,
        content_id: currentBanner.title,
        content_name: 'top_banner',
        index: GTM_NOTSET_STRING,
        section_index: '2',
        section_name: 'mypage_top_banner',
      });
    }

    if (currentBanner?.link) {
      window.open(currentBanner.link, '_blank');
    }
  };

  return (
    <section className={twMerge('w-full px-[16px]! pt-[4px]! pb-[12px]!', className)}>
      <RollingBanner
        bgColor="white"
        animationnDuration={4000}
        banners={banners}
        onClick={handleClickBanner}
      />
    </section>
  );
};
